import React, { useState, useMemo } from 'react';
import ReactDOM from 'react-dom';
import {
  X,
  Download,
  FileSpreadsheet,
  FileText,
  FileType,
  Loader2,
  CheckCircle,
  AlertCircle,
  AlertTriangle,
  Filter,
  Users,
} from 'lucide-react';
import * as XLSX from 'xlsx';
import type { StudentUser } from '@/services/userService';
import type { AcademicYear } from '@/services/academicPlanningService';

interface BulkExportCredentialsModalProps {
  students: StudentUser[];
  academicYears: AcademicYear[];
  currentAcademicYear: AcademicYear | null;
  onClose: () => void;
}

type ExportFormat = 'csv' | 'excel' | 'pdf';

/**
 * Modal for bulk exporting student credentials
 * Allows filtering by academic year and campus before export
 * Supports CSV, Excel (xlsx), and PDF formats
 */
export const BulkExportCredentialsModal: React.FC<BulkExportCredentialsModalProps> = ({
  students,
  academicYears,
  currentAcademicYear,
  onClose,
}) => {
  const [selectedFormat, setSelectedFormat] = useState<ExportFormat>('csv');
  const [selectedAcademicYearId, setSelectedAcademicYearId] = useState<string>(
    currentAcademicYear?.id || ''
  );
  const [selectedCampusId, setSelectedCampusId] = useState<string>('');
  const [selectedGroupId, setSelectedGroupId] = useState<string>('');
  const [exporting, setExporting] = useState(false);
  const [exportSuccess, setExportSuccess] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);
  const [showSecurityWarning, setShowSecurityWarning] = useState(false);

  // Get unique campuses from students
  const uniqueCampuses = useMemo(() => {
    const campusMap = new Map<string, { id: string; name: string; city: string }>();
    students.forEach((student) => {
      if (student.campus) {
        campusMap.set(student.campus.id, student.campus);
      }
    });
    return Array.from(campusMap.values()).sort((a, b) => a.name.localeCompare(b.name));
  }, [students]);

  // Get unique groups from students' active group assignments
  const uniqueGroups = useMemo(() => {
    const groupMap = new Map<string, { id: string; name: string }>();
    students.forEach((student) => {
      (student.groupes || []).forEach((groupe) => {
        groupMap.set(groupe.id, groupe);
      });
    });
    return Array.from(groupMap.values()).sort((a, b) => a.name.localeCompare(b.name));
  }, [students]);

  // Filter students based on selections
  const filteredStudents = useMemo(() => {
    return students.filter((student) => {
      // Filter by academic year
      if (selectedAcademicYearId && student.academicYear?.id !== selectedAcademicYearId) {
        return false;
      }
      // Filter by campus
      if (selectedCampusId && student.campus?.id !== selectedCampusId) {
        return false;
      }
      // Filter by group
      if (selectedGroupId && !(student.groupes || []).some((g) => g.id === selectedGroupId)) {
        return false;
      }
      // Only include students with credentials (tempPassword and haven't logged in)
      // Actually, include ALL students for the bulk export - admin might want all student codes
      return true;
    });
  }, [students, selectedAcademicYearId, selectedCampusId, selectedGroupId]);

  // Count students with temp passwords that haven't logged in
  const studentsWithCredentials = useMemo(() => {
    return filteredStudents.filter((s) => s.tempPassword && !s.lastLoginAt);
  }, [filteredStudents]);

  // Generate CSV content
  const generateCSV = (data: StudentUser[]): string => {
    const headers = [
      'Prénom',
      'Nom',
      'Code Étudiant',
      'Email (Identifiant)',
      'Filière',
      'Niveau',
      'Groupe',
      'Mot de passe temporaire',
    ];
    const rows = data.map((student) => [
      student.firstName,
      student.lastName,
      student.studentCode || student.username || '',
      student.email || '',
      student.filiereInscrit || '',
      student.niveauFormation?.toString() || '',
      (student.groupes || []).map((g) => g.name).join(' / '),
      student.tempPassword || '(déjà connecté)',
    ]);

    // Escape values and join
    const escapeCSV = (value: string) => {
      if (value.includes(',') || value.includes('"') || value.includes('\n')) {
        return `"${value.replace(/"/g, '""')}"`;
      }
      return value;
    };

    const csvContent = [
      headers.join(','),
      ...rows.map((row) => row.map(escapeCSV).join(',')),
    ].join('\n');

    // Add BOM for Excel to properly detect UTF-8
    return '\uFEFF' + csvContent;
  };

  // Generate proper XLSX file using SheetJS
  const generateExcelFile = (data: StudentUser[]): Blob => {
    // Prepare data for Excel
    const excelData = data.map((student) => ({
      'Prénom': student.firstName,
      'Nom': student.lastName,
      'Code Étudiant': student.studentCode || student.username || '',
      'Email (Identifiant)': student.email || '',
      'Filière': student.filiereInscrit || '',
      'Niveau': student.niveauFormation?.toString() || '',
      'Groupe': (student.groupes || []).map((g) => g.name).join(' / '),
      'Mot de passe temporaire': student.tempPassword || '(déjà connecté)',
    }));

    // Create workbook and worksheet
    const worksheet = XLSX.utils.json_to_sheet(excelData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Identifiants Étudiants');

    // Set column widths
    worksheet['!cols'] = [
      { wch: 15 }, // Prénom
      { wch: 15 }, // Nom
      { wch: 18 }, // Code Étudiant
      { wch: 25 }, // Filière
      { wch: 10 }, // Niveau
      { wch: 15 }, // Groupe
      { wch: 22 }, // Mot de passe temporaire
    ];

    // Generate Excel file as array buffer
    const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });

    return new Blob([excelBuffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    });
  };

  // Generate PDF (opens print dialog with formatted table)
  const generatePDF = (data: StudentUser[]) => {
    const printWindow = window.open('', '_blank');
    if (!printWindow) {
      setExportError("Impossible d'ouvrir la fenêtre d'impression. Vérifiez les bloqueurs de popups.");
      return;
    }

    const academicYearName = selectedAcademicYearId
      ? academicYears.find((y) => y.id === selectedAcademicYearId)?.name || ''
      : currentAcademicYear?.name || '';

    const campusName = selectedCampusId
      ? uniqueCampuses.find((c) => c.id === selectedCampusId)?.name || ''
      : 'Tous les campus';

    const tableRows = data
      .map(
        (student, index) => `
      <tr style="background-color: ${index % 2 === 0 ? '#ffffff' : '#f9fafb'}">
        <td style="padding: 8px; border: 1px solid #e5e7eb;">${student.firstName}</td>
        <td style="padding: 8px; border: 1px solid #e5e7eb;">${student.lastName}</td>
        <td style="padding: 8px; border: 1px solid #e5e7eb; font-family: monospace; font-weight: bold;">${student.studentCode || student.username || ''}</td>
        <td style="padding: 8px; border: 1px solid #e5e7eb; font-family: monospace;">${student.email || ''}</td>
        <td style="padding: 8px; border: 1px solid #e5e7eb;">${student.filiereInscrit || ''}</td>
        <td style="padding: 8px; border: 1px solid #e5e7eb; text-align: center;">${student.niveauFormation || ''}</td>
        <td style="padding: 8px; border: 1px solid #e5e7eb;">${(student.groupes || []).map((g) => g.name).join(' / ')}</td>
        <td style="padding: 8px; border: 1px solid #e5e7eb; font-family: monospace;">${student.tempPassword || '(déjà connecté)'}</td>
      </tr>`
      )
      .join('');

    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>Identifiants Étudiants - ${academicYearName}</title>
          <style>
            @page { size: landscape; margin: 1cm; }
            body {
              font-family: Arial, sans-serif;
              padding: 20px;
              font-size: 11px;
            }
            .header {
              text-align: center;
              margin-bottom: 20px;
              border-bottom: 2px solid #1e40af;
              padding-bottom: 15px;
            }
            .header h1 {
              color: #1e40af;
              margin: 0 0 10px 0;
              font-size: 18px;
            }
            .header .subtitle {
              color: #6b7280;
              font-size: 12px;
            }
            .info-bar {
              display: flex;
              justify-content: space-between;
              margin-bottom: 15px;
              padding: 10px;
              background: #f3f4f6;
              border-radius: 4px;
            }
            table {
              width: 100%;
              border-collapse: collapse;
              font-size: 10px;
            }
            th {
              background-color: #1e40af;
              color: white;
              padding: 10px 8px;
              text-align: left;
              font-weight: bold;
            }
            .footer {
              margin-top: 20px;
              text-align: center;
              color: #9ca3af;
              font-size: 10px;
              border-top: 1px solid #e5e7eb;
              padding-top: 10px;
            }
            .important-note {
              background: #fef3c7;
              border: 1px solid #f59e0b;
              border-radius: 4px;
              padding: 10px;
              margin: 15px 0;
              font-size: 11px;
              color: #92400e;
            }
            @media print {
              .no-print { display: none; }
            }
          </style>
        </head>
        <body>
          <div class="header">
            <h1>Identifiants de connexion - Étudiants</h1>
            <div class="subtitle">Plateforme NEXEDU</div>
          </div>

          <div class="info-bar">
            <span><strong>Année académique:</strong> ${academicYearName}</span>
            <span><strong>Campus:</strong> ${campusName}</span>
            <span><strong>Nombre d'étudiants:</strong> ${data.length}</span>
            <span><strong>Date:</strong> ${new Date().toLocaleDateString('fr-FR')}</span>
          </div>

          <div class="important-note">
            <strong>Important:</strong> Ce document contient des informations confidentielles.
            Les mots de passe temporaires doivent être changés lors de la première connexion.
          </div>

          <table>
            <thead>
              <tr>
                <th>Prénom</th>
                <th>Nom</th>
                <th>Code Étudiant</th>
                <th>Email (Identifiant)</th>
                <th>Filière</th>
                <th style="text-align: center;">Niveau</th>
                <th>Groupe</th>
                <th>Mot de passe temporaire</th>
              </tr>
            </thead>
            <tbody>
              ${tableRows}
            </tbody>
          </table>

          <div class="footer">
            <p>Document généré le ${new Date().toLocaleString('fr-FR')} - Plateforme NEXEDU</p>
            <p>Les étudiants marqués "(déjà connecté)" ont déjà changé leur mot de passe.</p>
          </div>
        </body>
      </html>
    `);

    printWindow.document.close();
    printWindow.print();
  };

  // Handle export
  const handleExport = async () => {
    setExporting(true);
    setExportError(null);
    setExportSuccess(false);

    try {
      const dataToExport = filteredStudents;

      if (dataToExport.length === 0) {
        setExportError('Aucun étudiant à exporter avec les filtres sélectionnés.');
        setExporting(false);
        return;
      }

      switch (selectedFormat) {
        case 'csv': {
          const csv = generateCSV(dataToExport);
          const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
          downloadBlob(blob, `identifiants_etudiants_${getDateSuffix()}.csv`);
          break;
        }
        case 'excel': {
          const blob = generateExcelFile(dataToExport);
          downloadBlob(blob, `identifiants_etudiants_${getDateSuffix()}.xlsx`);
          break;
        }
        case 'pdf': {
          generatePDF(dataToExport);
          break;
        }
      }

      setExportSuccess(true);
      setTimeout(() => {
        setExportSuccess(false);
      }, 3000);
    } catch (err) {
      console.error('Export error:', err);
      setExportError("Une erreur s'est produite lors de l'export.");
    } finally {
      setExporting(false);
    }
  };

  const downloadBlob = (blob: Blob, filename: string) => {
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const getDateSuffix = () => {
    const now = new Date();
    return `${now.getFullYear()}${String(now.getMonth() + 1).padStart(2, '0')}${String(now.getDate()).padStart(2, '0')}`;
  };

  const formatOptions: Array<{ value: ExportFormat; label: string; icon: React.ElementType; description: string }> = [
    { value: 'csv', label: 'CSV', icon: FileText, description: 'Compatible avec Excel, Google Sheets' },
    { value: 'excel', label: 'Excel', icon: FileSpreadsheet, description: 'Format natif Microsoft Excel' },
    { value: 'pdf', label: 'PDF', icon: FileType, description: 'Document imprimable' },
  ];

  // Security confirmation handlers
  const handleExportClick = () => {
    if (filteredStudents.length === 0) {
      setExportError('Aucun etudiant a exporter avec les filtres selectionnes.');
      return;
    }
    setShowSecurityWarning(true);
  };

  const confirmExport = () => {
    setShowSecurityWarning(false);
    handleExport();
  };

  const cancelSecurityWarning = () => {
    setShowSecurityWarning(false);
  };

  return ReactDOM.createPortal(
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl w-full max-w-lg overflow-hidden shadow-xl">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200 bg-gradient-to-r from-blue-50 to-white">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-100 rounded-lg">
              <Download className="text-blue-600" size={24} />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-gray-900">Exporter les identifiants</h2>
              <p className="text-sm text-gray-500">Télécharger les codes étudiants et mots de passe</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X size={20} />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Filters */}
          <div className="space-y-4">
            <div className="flex items-center gap-2 text-sm font-medium text-gray-700">
              <Filter size={16} />
              Filtres (optionnel)
            </div>

            <div className="grid grid-cols-2 gap-4">
              {/* Academic Year Filter */}
              <div>
                <label className="block text-sm text-gray-600 mb-1">Année académique</label>
                <select
                  value={selectedAcademicYearId}
                  onChange={(e) => setSelectedAcademicYearId(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="">Toutes les années</option>
                  {academicYears.map((year) => (
                    <option key={year.id} value={year.id}>
                      {year.name} {year.isCurrent && '(actuelle)'}
                    </option>
                  ))}
                </select>
              </div>

              {/* Campus Filter */}
              <div>
                <label className="block text-sm text-gray-600 mb-1">Campus</label>
                <select
                  value={selectedCampusId}
                  onChange={(e) => setSelectedCampusId(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="">Tous les campus</option>
                  {uniqueCampuses.map((campus) => (
                    <option key={campus.id} value={campus.id}>
                      {campus.name} - {campus.city}
                    </option>
                  ))}
                </select>
              </div>

              {/* Group Filter */}
              <div>
                <label className="block text-sm text-gray-600 mb-1">Groupe</label>
                <select
                  value={selectedGroupId}
                  onChange={(e) => setSelectedGroupId(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="">Tous les groupes</option>
                  {uniqueGroups.map((groupe) => (
                    <option key={groupe.id} value={groupe.id}>
                      {groupe.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Student count info */}
            <div className="flex items-center gap-2 p-3 bg-gray-50 rounded-lg">
              <Users size={18} className="text-gray-500" />
              <span className="text-sm text-gray-600">
                <strong>{filteredStudents.length}</strong> étudiant(s) sélectionné(s)
                {studentsWithCredentials.length > 0 && (
                  <span className="text-amber-600 ml-2">
                    ({studentsWithCredentials.length} avec mot de passe temporaire)
                  </span>
                )}
              </span>
            </div>
          </div>

          {/* Format Selection */}
          <div className="space-y-3">
            <label className="block text-sm font-medium text-gray-700">Format d'export</label>
            <div className="grid grid-cols-3 gap-3">
              {formatOptions.map((option) => {
                const Icon = option.icon;
                return (
                  <button
                    key={option.value}
                    onClick={() => setSelectedFormat(option.value)}
                    className={`flex flex-col items-center p-4 rounded-lg border-2 transition-colors ${
                      selectedFormat === option.value
                        ? 'border-blue-500 bg-blue-50'
                        : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                    }`}
                  >
                    <Icon
                      size={24}
                      className={selectedFormat === option.value ? 'text-blue-600' : 'text-gray-500'}
                    />
                    <span
                      className={`mt-2 font-medium text-sm ${
                        selectedFormat === option.value ? 'text-blue-700' : 'text-gray-700'
                      }`}
                    >
                      {option.label}
                    </span>
                    <span className="mt-1 text-xs text-gray-500 text-center">{option.description}</span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Export contents info */}
          <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
            <p className="text-sm text-blue-800">
              <strong>Colonnes exportées:</strong> Prénom, Nom, Code Étudiant (identifiant), Filière, Niveau, Groupe, Mot de passe temporaire
            </p>
          </div>

          {/* Status messages */}
          {exportError && (
            <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg">
              <AlertCircle size={18} className="text-red-600" />
              <span className="text-sm text-red-800">{exportError}</span>
            </div>
          )}

          {exportSuccess && (
            <div className="flex items-center gap-2 p-3 bg-green-50 border border-green-200 rounded-lg">
              <CheckCircle size={18} className="text-green-600" />
              <span className="text-sm text-green-800">Export réussi!</span>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-3 p-4 border-t border-gray-200 bg-gray-50">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
          >
            Annuler
          </button>
          <button
            onClick={handleExportClick}
            disabled={exporting || filteredStudents.length === 0}
            className="flex items-center gap-2 px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {exporting ? (
              <>
                <Loader2 size={18} className="animate-spin" />
                Export en cours...
              </>
            ) : (
              <>
                <Download size={18} />
                Exporter ({filteredStudents.length})
              </>
            )}
          </button>
        </div>

        {/* Security Warning Dialog */}
        {showSecurityWarning && (
          <div className="fixed inset-0 z-[60] bg-black bg-opacity-50 flex items-center justify-center p-4">
            <div className="bg-white p-6 rounded-xl shadow-xl max-w-md w-full">
              <div className="flex items-start gap-3">
                <div className="flex-shrink-0 p-2 bg-amber-100 rounded-full">
                  <AlertTriangle className="w-6 h-6 text-amber-500" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900">Attention - Donnees sensibles</h3>
                  <p className="text-sm text-gray-600 mt-1">
                    Ce fichier contient des identifiants confidentiels de {filteredStudents.length} etudiant(s). Assurez-vous de le stocker de maniere securisee et de ne pas le partager par email non chiffre.
                  </p>
                </div>
              </div>
              <div className="flex justify-end gap-3 mt-6">
                <button
                  onClick={cancelSecurityWarning}
                  className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Annuler
                </button>
                <button
                  onClick={confirmExport}
                  className="px-4 py-2 bg-amber-500 text-white rounded-lg hover:bg-amber-600 transition-colors"
                >
                  Continuer
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>,
    document.body
  );
};

export default BulkExportCredentialsModal;
